import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于快猫平台',
  description: '快猫是备受用户青睐的内容创作与短视频平台，了解我们的品牌故事、核心价值观与团队信息。',
  url: 'https://kuaimao.com/about',
  isPartOf: {
    '@type': 'WebSite',
    name: '快猫平台',
    url: 'https://kuaimao.com',
  },
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '快猫平台',
  url: 'https://kuaimao.com',
  description: '快猫是备受用户青睐的内容创作与短视频平台，汇聚海量优质创作者与精彩内容。',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '400-888-6666',
    contactType: 'customer service',
    availableLanguage: 'Chinese',
    areaServed: 'CN',
  },
  email: 'contact@kuaimao.com',
  foundingDate: '2020',
};

export default function AboutPage() {
  return (
    <>
      <SEOHead
        title="关于我们 - 快猫平台"
        description="了解快猫平台的品牌故事、核心价值、发展历程与联系方式。快猫是备受用户青睐的内容创作与短视频平台。"
        keywords="快猫,关于快猫,快猫平台介绍,快猫联系方式,快猫品牌故事"
        canonicalUrl="https://kuaimao.com/about"
        schemaJson={{ ...aboutSchema, ...orgSchema }}
      />
      <main className="min-h-screen bg-background-50 pt-16">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <div className="px-4 md:px-6 py-10 md:py-14">
            {/* Brand Intro */}
            <section className="mb-16 md:mb-20">
              <div className="max-w-3xl">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  关于快猫
                </h1>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                  快猫成立于2020年，是一家专注于内容创作与短视频领域的技术驱动型平台。我们致力于为每一位创作者提供最优质的创作工具和展示舞台，让优秀的创作被世界看见，让每一份才华都能找到发光的方式。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  经过多年的发展，快猫已成长为拥有超过1000万活跃创作者、5亿月活用户的领先内容平台。我们持续投入技术研发，先后推出了AI智能剪辑、AI文案生成、智能推荐系统等创新功能，不断降低创作门槛，提升创作效率。
                </p>
              </div>
            </section>

            {/* Core Values */}
            <section className="mb-16 md:mb-20">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                核心价值观
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {[
                  {
                    icon: 'ri-heart-line',
                    title: '创作者优先',
                    desc: '一切产品和服务的出发点都是为创作者创造价值，让创作者获得应有的回报与尊重。',
                  },
                  {
                    icon: 'ri-lightbulb-flash-line',
                    title: '技术驱动创新',
                    desc: '持续投入AI与前沿技术研发，用技术力量降低创作门槛，释放每个人的创作潜能。',
                  },
                  {
                    icon: 'ri-shield-check-line',
                    title: '责任与信任',
                    desc: '坚持内容安全与用户保护，打造健康、清朗、可信赖的平台内容生态。',
                  },
                  {
                    icon: 'ri-group-line',
                    title: '开放共赢',
                    desc: '与创作者、品牌方、合作伙伴建立开放共赢的生态系统，共同推动行业发展。',
                  },
                ].map((value, index) => (
                  <div
                    key={index}
                    className="p-6 rounded-lg bg-background-100 border border-background-200/60 text-center"
                  >
                    <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                      <i className={`${value.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-2">
                      {value.title}
                    </h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">
                      {value.desc}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* Development Timeline */}
            <section className="mb-16 md:mb-20">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-6 top-0 bottom-0 w-px bg-background-200"></div>
                <div className="flex flex-col gap-8">
                  {[
                    { year: '2020', event: '快猫平台正式上线，开放创作者入驻，首月注册用户突破50万' },
                    { year: '2021', event: '推出创作者激励计划，平台月活用户突破5000万，创作者数量达到100万' },
                    { year: '2022', event: '上线AI智能剪辑功能，推出电商带货系统，月活用户突破2亿' },
                    { year: '2023', event: '品牌合作系统上线，创作者生态全面升级，年交易规模突破100亿元' },
                    { year: '2024', event: 'AI创作助手全面推出，平台创作者突破800万，获得年度最佳内容平台奖' },
                    { year: '2025', event: '国际化战略启动，月活用户突破5亿，创作者数量突破1000万' },
                    { year: '2026', event: '深化AI技术应用，推出全新创作者成长体系，持续引领内容创作行业创新' },
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-5 md:gap-8 pl-10 md:pl-14 relative">
                      <div className="absolute left-[14px] md:left-[22px] top-1 w-2.5 h-2.5 rounded-full bg-accent-500 ring-4 ring-background-50"></div>
                      <div>
                        <span className="text-sm font-bold text-accent-600">{item.year}</span>
                        <p className="text-sm text-foreground-600 mt-0.5 leading-relaxed">
                          {item.event}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Reputation */}
            <section className="mb-16 md:mb-20">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                口碑声誉
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {[
                  {
                    title: '年度最佳内容平台',
                    source: '中国互联网协会',
                    year: '2025',
                  },
                  {
                    title: '用户满意度金奖',
                    source: '中国用户体验联盟',
                    year: '2025',
                  },
                  {
                    title: '最具创新力企业',
                    source: '科技产业研究院',
                    year: '2024',
                  },
                  {
                    title: '社会责任典范奖',
                    source: '中国企业社会责任联盟',
                    year: '2025',
                  },
                ].map((award, index) => (
                  <div
                    key={index}
                    className="p-5 rounded-lg bg-accent-50 border border-accent-200/40 text-center"
                  >
                    <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                      <i className="ri-award-line text-lg"></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-1">
                      {award.title}
                    </h3>
                    <p className="text-xs text-foreground-500">{award.source}</p>
                    <p className="text-xs text-accent-600 font-medium mt-1">{award.year}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact */}
            <section className="mb-16 md:mb-20">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                <div className="p-6 rounded-lg bg-background-100 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">电子邮箱</h3>
                  <p className="text-xs text-foreground-500 mb-0.5">商务合作：partner@kuaimao.com</p>
                  <p className="text-xs text-foreground-500">媒体联系：media@kuaimao.com</p>
                </div>
                <div className="p-6 rounded-lg bg-background-100 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">客服热线</h3>
                  <p className="text-xs text-foreground-500">400-888-6666</p>
                  <p className="text-xs text-foreground-400 mt-1">周一至周日 9:00 - 21:00</p>
                </div>
                <div className="p-6 rounded-lg bg-background-100 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">公司地址</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    上海市浦东新区张江高科技园区
                    <br />
                    快猫大厦 18层
                  </p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
    </>
  );
}